import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { MapPin, Unlock, Lightbulb, Flag, ArrowRight, Loader2, Music, PartyPopper } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const guessIcon = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 24 24" fill="#FF453A" stroke="#fff" stroke-width="1.3" style="filter:drop-shadow(0 0 10px rgba(255,69,58,0.8));"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3" fill="#fff" stroke="none"/></svg>`,
  iconSize: [38, 38], iconAnchor: [19, 36],
});

const targetIcon = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#34C759" stroke="#fff" stroke-width="1.3" style="filter:drop-shadow(0 0 12px rgba(52,199,89,0.9));"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3" fill="#fff" stroke="none"/></svg>`,
  iconSize: [40, 40], iconAnchor: [20, 38],
});

const fmtDistance = (m) => {
  const ft = m * 3.28084;
  return ft >= 2640 ? `${(ft / 5280).toFixed(1)} miles` : `${Math.round(ft)} feet`;
};

const ClickCapture = ({ onPick, disabled }) => {
  useMapEvents({ click: (e) => { if (!disabled) onPick(e.latlng); } });
  return null;
};

export default function PlayLennon() {
  const [quest, setQuest] = useState(null);
  const [idx, setIdx] = useState(0);
  const [guess, setGuess] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [solved, setSolved] = useState(null);
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    axios.get(`${API}/demo/lennon`)
      .then(({ data }) => setQuest(data))
      .catch(() => toast.error("Couldn't load the demo. Try refreshing."));
  }, []);

  const pin = quest?.pins?.[idx];
  const mapKey = useMemo(() => `map-${idx}`, [idx]);

  const submit = async () => {
    if (!guess) return toast.error("Tap the map to drop your pin first.");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/demo/lennon/attempt`, {
        pin_number: pin.pin_number, latitude: guess.lat, longitude: guess.lng,
      });
      setLastResult(data);
      if (data.result === "miss") {
        setAttempts((a) => a + 1);
      } else {
        setSolved(data.reveal);
        setTarget(data.target);
        setShowAnswer(false);
      }
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const giveUp = async () => {
    try {
      const { data } = await axios.post(`${API}/demo/lennon/giveup`, { pin_number: pin.pin_number });
      setSolved(data.reveal);
      setTarget(data.target);
      setLastResult(null);
      setShowAnswer(false);
    } catch (e) {
      toast.error("Something went wrong. Try again.");
    }
  };

  const next = () => {
    if (idx + 1 >= quest.pins.length) return setFinished(true);
    setIdx(idx + 1);
    setGuess(null); setAttempts(0); setLastResult(null); setSolved(null); setTarget(null); setShowAnswer(false);
  };

  if (!quest) {
    return (
      <main className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 size={26} className="animate-spin text-[#FF453A]" />
      </main>
    );
  }

  if (finished) {
    return (
      <main data-testid="demo-complete" className="pt-16 min-h-[92vh] flex items-center relative overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] rounded-full bg-[#34C759]/8 blur-3xl pointer-events-none" />
        <div className="relative max-w-xl mx-auto px-5 text-center py-20">
          <PartyPopper size={38} className="text-[#34C759] mx-auto mb-6" />
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter text-white">
            You finished the teaser.
          </h1>
          <p className="mt-5 text-lg text-[#c9cdd8]">
            That was {quest.demo_pins} of {quest.total_pins} pins in "{quest.title}." The full walk — Hamburg
            basements, the Abbey Road crossing, the Dakota — arrives when PinQuests launches.
          </p>
          <Link to="/early-access" data-testid="demo-complete-cta" className="mt-9 inline-flex items-center gap-2 rounded-full bg-[#FF453A] hover:bg-[#FF6961] transition-colors text-white font-semibold px-8 py-4 glow-red">
            Get Early Access <ArrowRight size={17} />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="play-lennon-page" className="pt-16 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF453A] mb-2 flex items-center gap-2">
              <Music size={13} /> Celebrity Walk — free teaser
            </p>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-white">{quest.title}</h1>
          </div>
          <p data-testid="demo-progress" className="font-heading font-bold text-[#9BA1B0]">
            Pin <span className="text-white">{pin.pin_number}</span> of {quest.demo_pins}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div className="rounded-2xl bg-[#131620] border border-[#2A3042] p-7" data-testid="demo-clue-card">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF453A] mb-3">The clue</p>
              <p className="text-[#e8eaf0] leading-relaxed font-heading text-lg">{pin.clue}</p>
            </div>

            {!solved && attempts >= 1 && (
              <div className="rounded-2xl bg-[#131620] border border-[#F59E0B]/40 p-6" data-testid="demo-hint-1">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B] mb-2 flex items-center gap-2"><Lightbulb size={13} /> Hint #1</p>
                <p className="text-sm text-[#c9cdd8]">{pin.hint_1}</p>
              </div>
            )}
            {!solved && attempts >= 2 && (
              <div className="rounded-2xl bg-[#131620] border border-[#F59E0B]/40 p-6" data-testid="demo-hint-2">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B] mb-2 flex items-center gap-2"><Lightbulb size={13} /> Hint #2</p>
                <p className="text-sm text-[#c9cdd8]">{pin.hint_2}</p>
              </div>
            )}

            {!solved && lastResult?.result === "miss" && (
              <div className="rounded-2xl border border-[#FF453A]/40 bg-[#FF453A]/5 p-6" data-testid="demo-miss-banner">
                <p className="font-heading font-semibold text-white mb-1">{lastResult.message}</p>
                <p className="text-sm text-[#9BA1B0]">Your pin was {fmtDistance(lastResult.distance_m)} from the target.</p>
              </div>
            )}

            {solved && (
              <div className="rounded-2xl border border-[#34C759]/40 bg-[#34C759]/5 p-7" data-testid="demo-unlock-card">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#34C759] mb-3 flex items-center gap-2"><Unlock size={13} /> Unlocked</p>
                {lastResult?.distance_m != null && (
                  <p className="text-xs text-[#9BA1B0] mb-2">{lastResult.message} ({fmtDistance(lastResult.distance_m)} from the target)</p>
                )}
                <h3 className="font-heading font-bold text-white text-xl mb-2">{solved.reveal_title}</h3>
                <p className="text-sm text-[#c9cdd8] leading-relaxed">{solved.reveal_story}</p>
                {solved.trivia && (
                  <div className="mt-4 rounded-xl bg-[#090A0F] border border-[#2A3042] p-4">
                    <p className="text-sm text-[#c9cdd8] font-medium">{solved.trivia.question}</p>
                    {showAnswer ? (
                      <p data-testid="demo-trivia-answer" className="text-sm text-[#34C759] mt-2 font-semibold">{solved.trivia.answer}</p>
                    ) : (
                      <button data-testid="demo-trivia-btn" onClick={() => setShowAnswer(true)} className="text-xs text-[#FF453A] hover:text-[#FF6961] font-bold mt-2 transition-colors">
                        Show answer
                      </button>
                    )}
                  </div>
                )}
                <button data-testid="demo-next-btn" onClick={next} className="mt-5 w-full rounded-full bg-[#34C759] hover:bg-[#4cd473] transition-colors text-[#04220e] font-heading font-bold py-3.5 flex items-center justify-center gap-2">
                  {idx + 1 >= quest.pins.length ? "Finish the teaser" : "Next pin"} <ArrowRight size={16} />
                </button>
              </div>
            )}

            {!solved && (
              <div className="flex flex-col gap-3">
                <button
                  data-testid="demo-lock-pin-btn" onClick={submit} disabled={submitting || !guess}
                  className="rounded-full bg-[#FF453A] hover:bg-[#FF6961] disabled:opacity-40 transition-colors text-white font-heading font-bold py-4 glow-red flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <><MapPin size={16} /> Lock In My Pin</>}
                </button>
                {attempts >= 2 && (
                  <button data-testid="demo-giveup-btn" onClick={giveUp} className="rounded-full border border-[#2A3042] hover:border-[#FF453A] transition-colors text-[#9BA1B0] text-sm font-semibold py-3 flex items-center justify-center gap-2">
                    <Flag size={14} /> Show me the answer
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-3">
            <div data-testid="demo-map" className="rounded-3xl overflow-hidden border-4 border-[#2A3042] shadow-2xl relative">
              <MapContainer key={mapKey} center={[53.4, -2.95]} zoom={11} scrollWheelZoom={true} style={{ height: "560px", width: "100%", zIndex: 0 }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <ClickCapture disabled={!!solved} onPick={setGuess} />
                {guess && <Marker position={guess} icon={guessIcon} />}
                {target && <Marker position={[target.latitude, target.longitude]} icon={targetIcon} />}
              </MapContainer>
              {!guess && !solved && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[400] rounded-full bg-[#090A0F]/85 backdrop-blur-xl border border-white/10 px-5 py-2.5">
                  <p className="text-sm text-[#c9cdd8]">Tap the map to drop your pin</p>
                </div>
              )}
            </div>
            <p className="mt-3 text-xs text-[#5a6070]">
              Zoom, pan and explore. Distance is checked on our servers — the answer never touches your browser.
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
